import { z } from 'zod'

import { goalConfigSchema } from '../misc/goal_config.ts'
import { kpiAppearanceSchema, pivotAppearanceSchema } from '../ui/kpi.ts'
import { type NodePropertyName, nodePropertyNameSchema } from './common.ts'
import { viewConfigDimensionSchema } from './dimension.ts'
import { viewConfigFilterNodeSchema, viewConfigFilterSchema } from './filter.ts'
import { viewConfigKpiMeasureSchema } from './measure.ts'
import { ViewConfigSheetSchema } from './sheet.ts'

export const viewConfigPivotSchema = z.object({
  rows: viewConfigDimensionSchema.array(), // rows, columnsのどちらかに、必ずMEASURE_NAMEが含まれる?
  columns: viewConfigDimensionSchema.array(),
  dimensionsWithoutRollup: viewConfigDimensionSchema.array().optional(), // 目標でのみ使用予定
  columnsForDrilldownComments: viewConfigDimensionSchema.array().optional(), // KPI ドリルダウン内の一覧パネルでコメントする際に使用する、元のダッシュボードのピボットの列
})
export type ViewConfigPivot = z.infer<typeof viewConfigPivotSchema>

const viewConfigPivotSorterSchema = z.object({
  columnKeys: z
    .object({
      key: z.string(),
      order: z.enum(['asc', 'desc']),
    })
    .array()
    .optional(),
})
export type ViewConfigPivotSorter = z.infer<typeof viewConfigPivotSorterSchema>

export const viewConfigKpiParameterSchema = z.object({
  pivot: viewConfigPivotSchema,
  sorter: viewConfigPivotSorterSchema.optional(), // 集計値をもとにレコードのソートを行う(dimensionのソートではないので注意)
  sorterForDrilldownComments: viewConfigPivotSorterSchema.optional(), // KPI ドリルダウン内の一覧パネルでコメントする際に使用する
  queryParameter: z.record(z.string(), z.union([z.string(), z.undefined()])).optional(), // XXX: この値はcoreのcompileでは使われず、ui側でparameterとしてマージされて使われる。挙動が分かりづらい。。
  invisibleKpiIds: z.string().array().optional(),
  dimensionFilterLeafs: viewConfigFilterSchema.array().optional(),
  goalConfig: goalConfigSchema.optional(),
  enableHistoricalGroupResolution: z.boolean().optional(),
})
export type ViewConfigKpiParameter = z.infer<typeof viewConfigKpiParameterSchema>
export const kpiPresetSchema = z.object({
  name: z.string(), // unique
  parameter: viewConfigKpiParameterSchema,
})
export type ViewConfigKpiPreset = z.infer<typeof kpiPresetSchema>

export const kpiFormulaNodeSchema = z.discriminatedUnion(`type`, [
  z.object({ type: z.literal(`empty`) }),
  z.object({ type: z.literal(`ref`), metricId: z.string() }),
])
export type KpiFormulaNode = z.infer<typeof kpiFormulaNodeSchema>

export const metricUserJoinAsSchema = z.enum([`id`, `email`, `name`])
export type MetricUserJoinAs = z.infer<typeof metricUserJoinAsSchema>

export const metricSchema = z.object({
  sheet: ViewConfigSheetSchema,
  measure: viewConfigKpiMeasureSchema,
  date: z
    .object({
      property: nodePropertyNameSchema,
    })
    .optional(),
  user: z
    .object({
      property: nodePropertyNameSchema,
      joinAs: metricUserJoinAsSchema,
    })
    .optional(),
})
export type Metric = z.infer<typeof metricSchema>

export const kpiFormulaNodeWithMetricSchema = z.discriminatedUnion(`type`, [
  z.object({ type: z.literal(`empty`) }),
  z.object({
    type: z.literal(`ref`),
    metricId: z.string(),
    metric: metricSchema,
  }),
])
export type KpiFormulaNodeWithMetric = z.infer<typeof kpiFormulaNodeWithMetricSchema>

//
// KPIの設定
//
export const viewConfigKpiSchema = z.object({
  type: z.literal(`kpi`),
  formula: kpiFormulaNodeSchema.optional(),
  sheet: ViewConfigSheetSchema.optional(), // 基本的な設定は、シートとして設定させる
  measure: viewConfigKpiMeasureSchema.optional(),
  date: z
    .object({
      property: nodePropertyNameSchema,
    })
    .optional(),
  user: z
    .object({
      property: nodePropertyNameSchema,
      joinAs: z.enum([`id`, `email`, `name`]),
    })
    .optional(),
  ui: z
    .object({
      kpi: kpiAppearanceSchema.omit({
        id: true,
        label: true,
        withDateField: true,
      }),
      appearance: pivotAppearanceSchema.optional(),
    })
    .optional(),
  presets: kpiPresetSchema.array().optional(),
  sheetExtraSetting: z
    .object({
      // KPIをドリルダウンして、シートとして表示するときだけに適用される設定。現状はfilterしかないので、実装側もfilterに特化した実装にしている
      // 2023/08/17 必要なのはフィルタではなくブロックフィルタだった :bow: migrationを後で行うが、以下はブロックフィルタ想定
      filterTree: viewConfigFilterNodeSchema.optional(),
    })
    .optional(),
  options: z
    .object({
      dimensionFieldsVisibility: z.boolean(),
    })
    .optional(),
})
export type ViewConfigKpi = z.infer<typeof viewConfigKpiSchema>

export const viewConfigKpiWithMetricSchema = viewConfigKpiSchema
  .omit({ sheet: true, measure: true, date: true, user: true })
  .extend({
    formula: kpiFormulaNodeWithMetricSchema,
  })
export type ViewConfigKpiWithMetric = z.infer<typeof viewConfigKpiWithMetricSchema>

export interface KpiDateAlias {
  alias: string
  property: NodePropertyName
}
